import { useEffect, useRef, useState } from 'react';
import { Button } from '@elysian/ui';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso } from '../invoices/shared';
import { exportBlob, exportFileName, FORMATS, saveFile, type ExportColumn, type ExportFormat } from './exportList';

/**
 * "Export" on a list (#9): Excel, CSV or JSON of the rows as shown, filters and
 * search applied. The file name says what and when, e.g. invoices-2026-10-18.xlsx.
 */
export function ExportMenu<T>({ name, columns, rows, disabled, all }: {
  /** What the list is, e.g. "Invoices"; used for the file and sheet name. */
  name: string;
  columns: ExportColumn<T>[];
  rows: T[];
  disabled?: boolean;
  /** When the list shows a page (#49, #51): every matching row, fetched for the file, and how many there are. */
  all?: { count: number; load: () => Promise<T[]> };
}) {
  const { today } = useSession();
  const toast = useToast((t) => t.show);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);

  async function go(format: ExportFormat) {
    setOpen(false);
    const day = iso(today());
    const file = exportFileName(name, format, day);
    let list = rows;
    try {
      if (all) list = await all.load();
    } catch (e) {
      toast(`Couldn’t export: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    saveFile(exportBlob(format, columns, list, name), file);
    toast(`${list.length} ${list.length === 1 ? 'row' : 'rows'} exported to ${file}`);
  }
  const count = all?.count ?? rows.length;

  return (
    <div className="el-menu" ref={box}>
      <Button size="sm" aria-haspopup="menu" aria-expanded={open} disabled={disabled || count === 0}
        title={count === 0 ? 'Nothing to export' : undefined} onClick={() => setOpen((o) => !o)}>
        Export
      </Button>
      {open && (
        <div className="el-menu-list" role="menu" aria-label={`Export ${name.toLowerCase()}`}>
          <div className="el-menu-note">{count} {count === 1 ? 'row' : 'rows'}{all ? ', every one that matches' : ', as shown'}</div>
          {FORMATS.map((f) => (
            <button key={f.key} type="button" role="menuitem" className="el-menu-item" onClick={() => void go(f.key)}>{f.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
